// components/common/ImageGallery.js
import React, { useState, useEffect, useCallback } from 'react';
import './ImageGallery.css';

function ImageGallery({ images = [], alt = '' }) {
  const [current, setCurrent] = useState(0);
  const [lightbox, setLightbox] = useState(false);

  // Si aucune image, affiche un placeholder
  const imgs = images.length > 0 ? images : [null];
  const hasMultiple = imgs.length > 1;

  const prev = useCallback((e) => {
    if (e) e.stopPropagation();
    setCurrent(c => (c - 1 + imgs.length) % imgs.length);
  }, [imgs.length]);

  const next = useCallback((e) => {
    if (e) e.stopPropagation();
    setCurrent(c => (c + 1) % imgs.length);
  }, [imgs.length]);

  // Keyboard navigation in lightbox
  useEffect(() => {
    if (!lightbox) return;
    const handler = (e) => {
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
      if (e.key === 'Escape') setLightbox(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [lightbox, prev, next]);

  // Lock scroll when lightbox open
  useEffect(() => {
    document.body.style.overflow = lightbox ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [lightbox]);

  return (
    <>
      {/* ── Main gallery ── */}
      <div className="gallery-root">
        <div className="gallery-main" onClick={() => setLightbox(true)}>
          {imgs[current] ? (
            <img src={imgs[current]} alt={`${alt} — photo ${current + 1}`} />
          ) : (
            <div className="gallery-placeholder">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <rect x="3" y="3" width="18" height="18" rx="2"/>
                <circle cx="8.5" cy="8.5" r="1.5"/>
                <polyline points="21 15 16 10 5 21"/>
              </svg>
              <span>Aucune photo disponible</span>
            </div>
          )}

          {imgs[current] && (
            <div className="gallery-zoom-hint">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"/>
                <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                <line x1="11" y1="8" x2="11" y2="14"/>
                <line x1="8" y1="11" x2="14" y2="11"/>
              </svg>
              Agrandir
            </div>
          )}

          {hasMultiple && (
            <>
              <button className="gallery-arrow gallery-arrow-left" onClick={prev}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <polyline points="15 18 9 12 15 6"/>
                </svg>
              </button>
              <button className="gallery-arrow gallery-arrow-right" onClick={next}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <polyline points="9 18 15 12 9 6"/>
                </svg>
              </button>
              <div className="gallery-counter">{current + 1} / {imgs.length}</div>
            </>
          )}
        </div>

        {/* ── Thumbnails ── */}
        {hasMultiple && (
          <div className="gallery-thumbs">
            {imgs.map((src, i) => (
              <button
                key={i}
                className={`gallery-thumb ${i === current ? 'active' : ''}`}
                onClick={() => setCurrent(i)}
              >
                {src ? (
                  <img src={src} alt={`${alt} miniature ${i + 1}`} />
                ) : (
                  <div className="gallery-thumb-placeholder" />
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ── Lightbox ── */}
      {lightbox && imgs[current] && (
        <div className="lightbox-overlay" onClick={() => setLightbox(false)}>
          <button className="lightbox-close" onClick={() => setLightbox(false)}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>

          <div className="lightbox-img-wrap" onClick={e => e.stopPropagation()}>
            <img src={imgs[current]} alt={`${alt} — photo ${current + 1}`} />
          </div>

          {hasMultiple && (
            <>
              <button className="lightbox-arrow lightbox-arrow-left" onClick={prev}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <polyline points="15 18 9 12 15 6"/>
                </svg>
              </button>
              <button className="lightbox-arrow lightbox-arrow-right" onClick={next}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <polyline points="9 18 15 12 9 6"/>
                </svg>
              </button>
              <div className="lightbox-counter">{current + 1} / {imgs.length}</div>
            </>
          )}
        </div>
      )}
    </>
  );
}

export default ImageGallery;
